/**
 * Subject and Quiz Constants
 *
 * Centralized constants for all subject-related data and quiz types across the application.
 * This ensures consistency and makes it easy to maintain subject information.
 */
// Unified question types across the application
export const QUESTION_TYPES = {
    MULTIPLE_CHOICE: 'multiple_choice',
    TRUE_FALSE: 'true_false',
    SHORT_ANSWER: 'short_answer',
    OPEN_ENDED: 'open_ended',
    SCENARIO_BASED: 'scenario_based',
    FILL_IN_GAP: 'fill_in_gap',
    MATCH_ANSWERS: 'match_answers',
    ANSWER_QUESTION: 'answer_question'
};
// Valid question types for API requests
export const VALID_QUESTION_TYPES = Object.values(QUESTION_TYPES);
// Question type display names
export const QUESTION_TYPE_LABELS = {
    [QUESTION_TYPES.MULTIPLE_CHOICE]: 'Multiple Choice',
    [QUESTION_TYPES.TRUE_FALSE]: 'True/False',
    [QUESTION_TYPES.SHORT_ANSWER]: 'Short Answer',
    [QUESTION_TYPES.OPEN_ENDED]: 'Open Ended',
    [QUESTION_TYPES.SCENARIO_BASED]: 'Scenario Based',
    [QUESTION_TYPES.FILL_IN_GAP]: 'Fill in the Gap',
    [QUESTION_TYPES.MATCH_ANSWERS]: 'Match Answers',
    [QUESTION_TYPES.ANSWER_QUESTION]: 'Answer Question'
};
// Education regions
export const EDUCATION_REGIONS = {
    UK: 'UK',
    US: 'US',
    UAE: 'UAE',
    CANADA: 'Canada',
    AUSTRALIA: 'Australia',
    INTERNATIONAL: 'International'
};
export const SUBJECT_CATEGORIES = {
    ACADEMIC: 'academic',
    PROFESSIONAL: 'professional',
    APPLIED: 'applied',
    HUMANITIES: 'humanities'
};
export const GRADE_LEVELS = {
    ELEMENTARY: 'elementary',
    MIDDLE: 'middle',
    HIGH: 'high',
    UNIVERSITY: 'university',
    PROFESSIONAL: 'professional'
};
export const COMPREHENSIVE_SUBJECTS = [
    // Core Academic Subjects
    {
        id: '550e8400-e29b-41d4-a716-446655440001',
        name: 'Mathematics',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Core mathematics including algebra, geometry, calculus, statistics'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440002',
        name: 'Science',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Natural sciences including biology, chemistry, physics, earth science'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440003',
        name: 'English',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'English language, literature, writing, and communication'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440004',
        name: 'History',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Historical studies, world history, and social studies'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440005',
        name: 'Geography',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Physical and human geography, world geography'
    },
    // Technology & Engineering
    {
        id: '550e8400-e29b-41d4-a716-446655440006',
        name: 'Computer Science',
        category: SUBJECT_CATEGORIES.APPLIED,
        gradeLevels: [GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Programming, algorithms, software development, AI/ML'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440007',
        name: 'Engineering',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.UNIVERSITY],
        description: 'Mechanical, electrical, civil, and other engineering disciplines'
    },
    // Health & Medicine
    {
        id: '550e8400-e29b-41d4-a716-446655440008',
        name: 'Medicine and Health',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.UNIVERSITY],
        description: 'Medical sciences, anatomy, physiology, public health'
    },
    // Business & Economics
    {
        id: '550e8400-e29b-41d4-a716-446655440009',
        name: 'Business and Economics',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Business management, economics, finance, marketing'
    },
    // Arts & Humanities
    {
        id: '550e8400-e29b-41d4-a716-446655440010',
        name: 'Arts',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Fine arts, music, theater, visual arts, design'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440012',
        name: 'Philosophy and Religion',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Philosophy, ethics, religious studies, theology'
    },
    // Social Sciences
    {
        id: '550e8400-e29b-41d4-a716-446655440013',
        name: 'Social Sciences',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Psychology, sociology, anthropology, political science'
    },
    // Law & Professional
    {
        id: '550e8400-e29b-41d4-a716-446655440014',
        name: 'Law',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.UNIVERSITY],
        description: 'Legal studies, constitutional law, criminal law, civil law'
    },
    // Environmental & Agriculture
    {
        id: '550e8400-e29b-41d4-a716-446655440015',
        name: 'Environmental Studies',
        category: SUBJECT_CATEGORIES.APPLIED,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Environmental science, sustainability, climate change'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440016',
        name: 'Agriculture',
        category: SUBJECT_CATEGORIES.APPLIED,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Agricultural sciences, crop science, animal science'
    },
    // Additional Academic Subjects
    {
        id: '550e8400-e29b-41d4-a716-446655440017',
        name: 'Physics',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Classical mechanics, quantum physics, thermodynamics'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440018',
        name: 'Chemistry',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Organic chemistry, inorganic chemistry, biochemistry'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440019',
        name: 'Biology',
        category: SUBJECT_CATEGORIES.ACADEMIC,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Cell biology, genetics, ecology, microbiology'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440020',
        name: 'Literature',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'World literature, literary analysis, creative writing'
    },
    // Professional Development
    {
        id: '550e8400-e29b-41d4-a716-446655440021',
        name: 'Professional Development',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.UNIVERSITY, GRADE_LEVELS.PROFESSIONAL],
        description: 'Career skills, leadership, communication, project management'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440022',
        name: 'Test Preparation',
        category: SUBJECT_CATEGORIES.PROFESSIONAL,
        gradeLevels: [GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'SAT, ACT, GRE, GMAT, LSAT, MCAT preparation'
    },
    // Specialized Subjects
    {
        id: '550e8400-e29b-41d4-a716-446655440023',
        name: 'Music',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Music theory, composition, performance, music history'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440024',
        name: 'Physical Education',
        category: SUBJECT_CATEGORIES.APPLIED,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH],
        description: 'Sports, fitness, health education, athletics'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440025',
        name: 'Technology',
        category: SUBJECT_CATEGORIES.APPLIED,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Digital literacy, computer applications, IT skills'
    },
    /* World languages — split from legacy umbrella "Languages" subject */
    {
        id: '550e8400-e29b-41d4-a716-446655440026',
        name: 'Spanish',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Spanish: conversation, grammar, reading, writing, listening, and literature'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440027',
        name: 'French',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'French: conversation, grammar, reading, writing, listening, and literature'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440028',
        name: 'Mandarin Chinese',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Mandarin: Pinyin, characters, tones, speaking, listening, reading, and writing'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440029',
        name: 'Arabic',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Arabic: Modern Standard Arabic, script, literacy, and conversation foundations'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440030',
        name: 'Hindi',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Hindi: Devanagari script, grammar, vocabulary, and comprehension'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440031',
        name: 'Portuguese',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Portuguese: conversation, grammar, and reading (Brazilian and European contexts)'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440032',
        name: 'German',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'German: conversation, grammar, reading, writing, and listening'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440033',
        name: 'Japanese',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Japanese: Hiragana, Katakana, Kanji foundations, and JLPT-aligned skills'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440034',
        name: 'Italian',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Italian: conversation, grammar, listening, reading, and cultural context'
    },
    {
        id: '550e8400-e29b-41d4-a716-446655440035',
        name: 'Korean',
        category: SUBJECT_CATEGORIES.HUMANITIES,
        gradeLevels: [GRADE_LEVELS.ELEMENTARY, GRADE_LEVELS.MIDDLE, GRADE_LEVELS.HIGH, GRADE_LEVELS.UNIVERSITY],
        description: 'Korean: Hangul, grammar, honorifics, speaking, and listening'
    }
];
// Helper functions to get subjects by category
export const SUBJECTS_BY_CATEGORY = {
    [SUBJECT_CATEGORIES.ACADEMIC]: COMPREHENSIVE_SUBJECTS.filter(s => s.category === SUBJECT_CATEGORIES.ACADEMIC),
    [SUBJECT_CATEGORIES.PROFESSIONAL]: COMPREHENSIVE_SUBJECTS.filter(s => s.category === SUBJECT_CATEGORIES.PROFESSIONAL),
    [SUBJECT_CATEGORIES.APPLIED]: COMPREHENSIVE_SUBJECTS.filter(s => s.category === SUBJECT_CATEGORIES.APPLIED),
    [SUBJECT_CATEGORIES.HUMANITIES]: COMPREHENSIVE_SUBJECTS.filter(s => s.category === SUBJECT_CATEGORIES.HUMANITIES)
};
// Helper functions to get subjects by grade level
export const SUBJECTS_BY_GRADE_LEVEL = {
    [GRADE_LEVELS.ELEMENTARY]: COMPREHENSIVE_SUBJECTS.filter(s => s.gradeLevels.includes(GRADE_LEVELS.ELEMENTARY)),
    [GRADE_LEVELS.MIDDLE]: COMPREHENSIVE_SUBJECTS.filter(s => s.gradeLevels.includes(GRADE_LEVELS.MIDDLE)),
    [GRADE_LEVELS.HIGH]: COMPREHENSIVE_SUBJECTS.filter(s => s.gradeLevels.includes(GRADE_LEVELS.HIGH)),
    [GRADE_LEVELS.UNIVERSITY]: COMPREHENSIVE_SUBJECTS.filter(s => s.gradeLevels.includes(GRADE_LEVELS.UNIVERSITY)),
    [GRADE_LEVELS.PROFESSIONAL]: COMPREHENSIVE_SUBJECTS.filter(s => s.gradeLevels.includes(GRADE_LEVELS.PROFESSIONAL))
};
// Utility functions
export const getSubjectById = (id) => {
    return COMPREHENSIVE_SUBJECTS.find(subject => subject.id === id);
};
export const getSubjectByName = (name) => {
    return COMPREHENSIVE_SUBJECTS.find(subject => subject.name.toLowerCase() === name.toLowerCase());
};
export const getSubjectsByCategory = (category) => {
    return SUBJECTS_BY_CATEGORY[category] || [];
};
export const getSubjectsByGradeLevel = (gradeLevel) => {
    return SUBJECTS_BY_GRADE_LEVEL[gradeLevel] || [];
};
export const searchSubjects = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return COMPREHENSIVE_SUBJECTS.filter(subject => subject.name.toLowerCase().includes(term) ||
        subject.description?.toLowerCase().includes(term));
};
// Fallback subjects for when API fails
export const FALLBACK_SUBJECTS = [
    { id: 'math', name: 'Mathematics' },
    { id: 'science', name: 'Science' },
    { id: 'english', name: 'English' },
    { id: 'history', name: 'History' },
    { id: 'art', name: 'Art' },
    { id: 'computer-science', name: 'Computer Science' },
];
